import React, { useMemo, useState } from 'react';
import { LayoutList, LayoutGrid, Settings2, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
import { mockLoanOfficers, LoanOfficer } from './data/mockData';
import { DashboardHeader, FilterState } from './components/DashboardHeader';
import { KPICards } from './components/KPICards';
import { PerformanceTable, SortConfig } from './components/PerformanceTable';
import { LoanOfficerCards } from './components/LoanOfficerCards';
import { CoachingPanel } from './components/CoachingPanel';
import { exportToCSV, getScaledLoanOfficer, computeKPIsFromLOs } from './utils/helpers';
type ViewMode = 'table' | 'cards';
export const ALL_COLUMNS = [{
  id: 'totalCalls',
  label: 'Total Calls'
}, {
  id: 'totalEngagedCalls',
  label: 'Total Engaged Calls'
}, {
  id: 'conversations60Plus',
  label: 'Conversations 60+ sec'
}, {
  id: 'totalTalkTime',
  label: 'Total Talk Time'
}, {
  id: 'avgTalkTimePerCall',
  label: 'Avg Talk Time per Call'
}, {
  id: 'avgAiCallScore',
  label: 'Avg AI Call Score'
}, {
  id: 'totalTransfers',
  label: 'Total Transfers'
}, {
  id: 'qualifiedTransfers',
  label: 'Qualified Transfers'
}, {
  id: 'transferRate',
  label: 'Transfer Rate'
}, {
  id: 'qualifiedTransferPct',
  label: 'Qualified Transfer %'
}, {
  id: 'failedTransfers',
  label: 'Failed Transfers'
}, {
  id: 'failedTransferPct',
  label: 'Failed Transfer %'
}, {
  id: 'transferToSubmissionPct',
  label: 'Transfer → Submission %'
}, {
  id: 'submissionToSignaturePct',
  label: 'Submission → Signature %'
}, {
  id: 'activePipelineCount',
  label: 'Active Pipeline Count'
}, {
  id: 'activePipelineValue',
  label: 'Active Pipeline $'
}, {
  id: 'documentsCollected',
  label: 'Documents Collected'
}, {
  id: 'docsCollectedPct',
  label: 'Docs Collected %'
}];
export function App() {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [filters, setFilters] = useState<FilterState>({
    dateRange: '30D',
    team: 'All',
    cohort: 'All',
    roleStage: 'All',
    showFlaggedOnly: false,
    searchQuery: '',
    customStartDate: '',
    customEndDate: ''
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'activePipelineValue',
    direction: 'desc'
  });
  const [selectedLO, setSelectedLO] = useState<LoanOfficer | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(ALL_COLUMNS.map(c => c.id));
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  // Mock last updated time
  const lastUpdated = useMemo(() => {
    const now = new Date();
    return `Today at ${now.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  }, []);
  // Extract unique filter options
  const teams = useMemo(() => {
    return Array.from(new Set(mockLoanOfficers.map(lo => lo.team)));
  }, []);
  const cohorts = useMemo(() => {
    return Array.from(new Set(mockLoanOfficers.map(lo => lo.cohort)));
  }, []);
  const roleStages = useMemo(() => {
    return Array.from(new Set(mockLoanOfficers.map(lo => lo.roleStage)));
  }, []);
  // Filter and sort data
  const filteredAndSortedLOs = useMemo(() => {
    // Scale data based on date range
    let result = mockLoanOfficers.map(lo => getScaledLoanOfficer(lo, filters.dateRange));
    // Apply Filters
    if (filters.team !== 'All') {
      result = result.filter(lo => lo.team === filters.team);
    }
    if (filters.cohort !== 'All') {
      result = result.filter(lo => lo.cohort === filters.cohort);
    }
    if (filters.roleStage !== 'All') {
      result = result.filter(lo => lo.roleStage === filters.roleStage);
    }
    if (filters.showFlaggedOnly) {
      result = result.filter(lo => lo.coachingFlags.length > 0);
    }
    if (filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(lo => lo.name.toLowerCase().includes(query));
    }
    // Apply Sort
    result.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [filters, sortConfig]);
  // Compute KPIs from filtered LOs
  const computedKPIs = useMemo(() => {
    return computeKPIsFromLOs(filteredAndSortedLOs);
  }, [filteredAndSortedLOs]);
  const handleSort = (key: keyof LoanOfficer) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };
  const handleExport = () => {
    exportToCSV(filteredAndSortedLOs, `Agent_Performance_${filters.dateRange}`);
  };
  const toggleColumn = (colId: string) => {
    setVisibleColumns(prev => prev.includes(colId) ? prev.filter(id => id !== colId) : [...prev, colId]);
  };
  return <div className="min-h-screen bg-gray-50 font-sans" data-id="element-0">
      <DashboardHeader filters={filters} onFilterChange={setFilters} teams={teams} cohorts={cohorts} roleStages={roleStages} onExport={handleExport} lastUpdated={lastUpdated} data-id="element-1" />

      <main className="max-w-[1600px] mx-auto px-6 pb-12" data-id="element-2">
        <KPICards kpis={computedKPIs} data-id="element-3" />

        {/* View Controls */}
        <div className="flex justify-between items-center mb-4 mt-8" data-id="element-4">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-5">Agent Performance</h2>

          <div className="flex items-center gap-3" data-id="element-6">
            <div className="relative" data-id="element-7">
              <button onClick={() => setShowColumnSelector(!showColumnSelector)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-8">
                <Settings2 className="w-4 h-4" data-id="element-9" />
                Columns
              </button>

              {showColumnSelector && <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 z-20 p-2" data-id="element-10">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wider px-3 py-2 border-b border-gray-100 mb-1" data-id="element-11">
                    Visible Columns
                  </div>
                  <div className="max-h-64 overflow-y-auto" data-id="element-12">
                    {ALL_COLUMNS.map(col => <label key={col.id} className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer" data-id="element-13">
                        <input type="checkbox" checked={visibleColumns.includes(col.id)} onChange={() => toggleColumn(col.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3" data-id="element-14" />
                        <span className="text-sm text-gray-700" data-id="element-15">
                          {col.label}
                        </span>
                      </label>)}
                  </div>
                </div>}
            </div>

            {viewMode === 'cards' && <div className="flex items-center gap-1.5" data-id="element-16">
                <select value={sortConfig.key} onChange={e => setSortConfig({
              key: e.target.value as keyof LoanOfficer,
              direction: sortConfig.direction
            })} className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block px-3 py-1.5 outline-none" data-id="element-17">
                  <option value="activePipelineValue" data-id="element-18">
                    Sort by Pipeline $
                  </option>
                  <option value="totalTransfers" data-id="element-19">Sort by Transfers</option>
                  <option value="transferRate" data-id="element-20">Sort by Transfer Rate</option>
                  <option value="totalCalls" data-id="element-21">Sort by Total Calls</option>
                  <option value="failedTransferPct" data-id="element-22">
                    Sort by Failed Transfer %
                  </option>
                </select>
                <button onClick={() => setSortConfig(prev => ({
              ...prev,
              direction: prev.direction === 'desc' ? 'asc' : 'desc'
            }))} className="flex items-center justify-center w-8 h-8 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" title={sortConfig.direction === 'desc' ? 'Descending — click for Ascending' : 'Ascending — click for Descending'} data-id="element-23">
                  {sortConfig.direction === 'desc' ? <ArrowDownIcon className="w-4 h-4 text-gray-700" data-id="element-24" /> : <ArrowUpIcon className="w-4 h-4 text-gray-700" data-id="element-25" />}
                </button>
              </div>}

            <div className="flex bg-gray-200 p-1 rounded-lg" data-id="element-26">
              <button onClick={() => setViewMode('table')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} title="Table View" data-id="element-27">
                <LayoutList className="w-5 h-5" data-id="element-28" />
              </button>
              <button onClick={() => setViewMode('cards')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} title="Card View" data-id="element-29">
                <LayoutGrid className="w-5 h-5" data-id="element-30" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        {filteredAndSortedLOs.length === 0 ? <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-31">
            <p className="text-gray-500" data-id="element-32">
              No Agents match the current filters.
            </p>
            <button onClick={() => setFilters({
          dateRange: '30D',
          team: 'All',
          cohort: 'All',
          roleStage: 'All',
          showFlaggedOnly: false,
          searchQuery: '',
          customStartDate: '',
          customEndDate: ''
        })} className="mt-4 text-blue-600 hover:text-blue-800 font-medium" data-id="element-33">
              Clear Filters
            </button>
          </div> : viewMode === 'table' ? <PerformanceTable loanOfficers={filteredAndSortedLOs} sortConfig={sortConfig} onSort={handleSort} onSelectLO={setSelectedLO} dateRange={filters.dateRange} visibleColumns={visibleColumns} data-id="element-34" /> : <LoanOfficerCards loanOfficers={filteredAndSortedLOs} onSelectLO={setSelectedLO} dateRange={filters.dateRange} visibleColumns={visibleColumns} data-id="element-35" />}
      </main>

      {/* Slide-out Panel */}
      <CoachingPanel selectedLO={selectedLO} isOpen={selectedLO !== null} onClose={() => setSelectedLO(null)} data-id="element-36" />
    </div>;
}